import React, { createContext, useContext, useState, useEffect } from 'react';

const getEnvValue = (key) => {
  if (typeof import.meta !== 'undefined' && import.meta.env && key in import.meta.env) {
    return import.meta.env[key];
  }

  if (typeof process !== 'undefined' && process.env && key in process.env) {
    return process.env[key];
  }

  return '';
};

const envDefaults = {
  supabaseUrl: (getEnvValue('VITE_SUPABASE_URL') || '').trim(),
  supabaseAnonKey: (getEnvValue('VITE_SUPABASE_ANON_KEY') || '').trim(),
  thoughtlyApiKey: (getEnvValue('VITE_THOUGHTLY_API_KEY') || '').trim(),
  thoughtlyTeamId: (getEnvValue('VITE_THOUGHTLY_TEAM_ID') || '').trim(),
  brevoApiKey: (getEnvValue('VITE_BREVO_API_KEY') || '').trim()
};

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    // Database settings
    supabaseUrl: envDefaults.supabaseUrl,
    supabaseAnonKey: envDefaults.supabaseAnonKey,

    // API settings
    thoughtlyApiKey: envDefaults.thoughtlyApiKey,
    thoughtlyTeamId: envDefaults.thoughtlyTeamId,
    brevoApiKey: envDefaults.brevoApiKey,

    // Email settings
    emailSignature: '',
    defaultEmailTemplate: '',
    
    // Call settings
    callSettings: {
      defaultVoice: 'natural',
      recordCalls: true,
      transcribeConversations: true
    },
    
    // Notification settings
    notifications: {
      emailCampaigns: true,
      callResults: true,
      systemUpdates: false
    },
    
    // General settings
    timezone: 'UTC',
    theme: 'light'
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('prosper-crm-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          ...parsed,
          ...envDefaults
        }));
      } catch (error) {
        console.error('Failed to parse saved settings', error);
        setSettings(prev => ({ ...prev, ...envDefaults }));
      }
    } else if (Object.values(envDefaults).some(Boolean)) {
      setSettings(prev => ({
        ...prev,
        ...envDefaults
      }));
    }
  }, []);

  // Save settings to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem('prosper-crm-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to persist settings', error);
    }
  }, [settings]);

  const updateSettings = (newSettings) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };

  const resetSettings = () => {
    setSettings({
      supabaseUrl: envDefaults.supabaseUrl,
      supabaseAnonKey: envDefaults.supabaseAnonKey,
      thoughtlyApiKey: envDefaults.thoughtlyApiKey,
      thoughtlyTeamId: envDefaults.thoughtlyTeamId,
      brevoApiKey: envDefaults.brevoApiKey,
      emailSignature: '',
      defaultEmailTemplate: '',
      callSettings: {
        defaultVoice: 'natural',
        recordCalls: true,
        transcribeConversations: true
      },
      notifications: {
        emailCampaigns: true,
        callResults: true,
        systemUpdates: false
      },
      timezone: 'UTC',
      theme: 'light'
    });
  };

  const isConfigured = () => {
    return settings.thoughtlyApiKey && settings.thoughtlyTeamId && settings.brevoApiKey;
  };

  const isSupabaseConfigured = () => {
    return settings.supabaseUrl && settings.supabaseAnonKey;
  };

  const value = {
    settings,
    updateSettings,
    resetSettings,
    isConfigured,
    isSupabaseConfigured
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};